import React, { useEffect, useMemo, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { EventEmitter } from '@meitu/saas-editor-core/es/framework';
import { groupAllText } from '@meitu/saas-editor-core/es/layers/group-layer/utils/layer';
import { SHORTCUT_FUNC_MAP } from '@meitu/saas-editor-core/es/plugins/context-menu';
import { StageToolbar } from '@meitu/saas-editor-core/es/plugins/stage-toolbar';
import { HIERARCHY_EVENTS } from '@meitu/saas-editor-core/es/plugins/stage-toolbar/hierarchy';
import {
    GroupRoleType,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    LayerType,
} from '@meitu/saas-editor-core/es/version-types';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { HeaderToolbarContext } from '../context';
import { ICallback } from '../types';
import Toolbar, { ToolBarType } from './com-toolbar/toolbar';
import ReplaceInspector from './replace-inspector';
import ThumbnailController from './thumbnail-controller';
import TitleController from './title-controller';
import './header-toolbar.less';

export type HeaderToolbarProps = React.ComponentProps<'div'> & {
    id: string;
    editor: Editor;
    isIntl?: boolean;
    events?: EventEmitter;
    inGroup?: boolean;
    BrandTextsPopup?: () => React.ReactNode;
    specifyToolBar?: string[];
    hideToolbar?: string[];
} & ICallback & {
        onMakeGroup?: () => void;
        onBrokeGroup?: () => void;
        onEditText?: () => void;
        onAI?: (func: string) => void;
    };

// 不需要复制按钮的图层类型
const noCopyBtnLayerTypes = [LayerType.WATERMARK];

type IActiveLayerProps = {
    layerType: LayerType;
    lock: boolean;
    textLayerId: string | null;
    thumbnail: string | null;
    groupAllText: boolean;
};

const Divider = () => {
    return <div className="c-header-toolbar-divider" />;
};

const HeaderToolbar: React.FC<HeaderToolbarProps> = (props) => {
    const {
        editor,
        inGroup,
        id,
        isIntl,
        events,
        style,
        BrandTextsPopup,
        onLock,
        onSort,
        onCopy,
        onDelete,
        beforeDelete,
        onCopyBrush,
        onApplyBrush,
        onMakeGroup,
        onBrokeGroup,
        onEditText,
        onAI,
        onCopyImageEffect,
        specifyToolBar = Object.values(ToolBarType),
    } = props;

    const [activeLayerProps, setActiveLayerProps] = useState<IActiveLayerProps | null>(null);
    const [role, setRole] = useState<GroupRoleType | null>(null);
    const [hideSort, setHideSort] = useState<boolean>(!editor.getInstance(StageToolbar).getHierarchyAvailability());

    function getActiveLayerProps(layer: ILayerProps | null, id: string) {
        const currentLayer = layer || editor.stage.getLayer(id);
        if (!currentLayer) return;
        setActiveLayerProps({
            layerType: currentLayer.layerType,
            lock: currentLayer.lock,
            textLayerId: currentLayer.layerType === LayerType.TEXT_LAYER ? currentLayer.id : null,
            thumbnail:
                currentLayer.layerType === LayerType.IMAGE_LAYER ? (currentLayer.data as IImageLayerProps).url : null,
            groupAllText: groupAllText(currentLayer) && currentLayer.layerType === LayerType.GROUP_LAYER,
        });
    }

    useEffect(() => {
        const handler = editor.shortcutMenu.onShortcutEvent((func) => {
            if (func === SHORTCUT_FUNC_MAP.delete) {
                onDelete?.();
            } else if (func === SHORTCUT_FUNC_MAP.createCopy) {
                onCopy?.();
            } else if (func === SHORTCUT_FUNC_MAP.makeGroup) {
                onMakeGroup?.();
            } else if (func === SHORTCUT_FUNC_MAP.brokeGroup) {
                onBrokeGroup?.();
            } else if (func === SHORTCUT_FUNC_MAP.editText) {
                onEditText?.();
            } else if (func === SHORTCUT_FUNC_MAP.textBrush) {
                onCopyBrush?.();
            } else if (func === SHORTCUT_FUNC_MAP.imageBrush) {
                onCopyImageEffect?.();
            } else if (
                func === SHORTCUT_FUNC_MAP.AI_TEXT ||
                func === SHORTCUT_FUNC_MAP.AI_MODIFY_IMAGE ||
                func === SHORTCUT_FUNC_MAP.AI_EXPEND_IMAGE ||
                func === SHORTCUT_FUNC_MAP.AI_GENERATE_IMAGE
            ) {
                onAI?.(func);
            }
        });
        return () => editor.off(handler);
    }, []);

    // 监听图层熟悉变化
    useEffect(() => {
        const handler = editor.stage.onLayerChange(() => {
            getActiveLayerProps(null, id);
        }, id);

        const layer = editor.stage.getLayer(id);
        if (layer && layer.layerType === LayerType.GROUP_LAYER) {
            const extension = (layer.data as IGroupLayerProps).extension;
            if (extension?.role) {
                setRole(extension.role);
            } else setRole(null);
        } else {
            setRole(null);
        }

        return () => {
            editor.off(handler);
        };
    }, [id]);

    // 监听激活图层变化
    useEffect(() => {
        getActiveLayerProps(null, id);
        const handler = editor.active.onChange((layer) => {
            setHideSort(!editor.getInstance(StageToolbar).getHierarchyAvailability());
            getActiveLayerProps(layer, id);
        });

        return () => {
            editor.off(handler);
        };
    }, [id]);

    const pickLayerProps = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            return {
                pickLayerType: layer.layerType,
                parentId: layer.parentId,
                textLayerId: layer.layerType === LayerType.TEXT_LAYER ? layer.id : null,
                thumbnail: layer.layerType === LayerType.IMAGE_LAYER ? (layer.data as IImageLayerProps).url : null,
            };
        },
        false,
    );

    useEffect(() => {
        const handler = editor.on(HIERARCHY_EVENTS.TOGGLE_AVAILABILITY, (flag: boolean) => {
            setHideSort(!flag);
        });

        return () => {
            editor.off(handler);
        };
    }, []);

    const finalToolBar = useMemo(() => {
        return specifyToolBar.filter((type) => type !== ToolBarType.SORT || !hideSort);
    }, [specifyToolBar, hideSort]);

    //如果是组合的情况下，默认展示组合的组件。
    if (!inGroup && pickLayerProps?.parentId) return null;

    if (!inGroup && activeLayerProps?.groupAllText) return null;

    if (!activeLayerProps) return null;

    const { layerType, lock } = activeLayerProps;

    const hideCopyBtn = noCopyBtnLayerTypes.includes(layerType);
    const textLayerId = activeLayerProps.textLayerId || pickLayerProps?.textLayerId;

    const isImageLayer = pickLayerProps
        ? pickLayerProps.pickLayerType === LayerType.IMAGE_LAYER
        : activeLayerProps.layerType === LayerType.IMAGE_LAYER;

    const thumbnail = pickLayerProps?.thumbnail || activeLayerProps.thumbnail;

    // const role =

    const params = {
        id,
        textLayerId: textLayerId || null,
        lock,
        isIntl,
        layerType,
        thumbnail,
        pickLayerType: pickLayerProps?.pickLayerType || null,
        hideCopyBtn,
        events,
        role,
        onLock,
        onSort,
        onCopyBrush,
        onApplyBrush,
        onCopy,
        onDelete,
        beforeDelete,
        onCopyImageEffect,
    };

    return (
        <HeaderToolbarContext.Provider value={params}>
            <div className="c-header-toolbar" style={style}>
                <div className="c-header-toolbar-container">
                    <ThumbnailController />
                    <div className="c-header-toolbar-content">
                        <TitleController />
                        {BrandTextsPopup && BrandTextsPopup()}
                        <div className="c-header-toolbar-actions">
                            {isImageLayer ? <ReplaceInspector hideIcon={isIntl && finalToolBar?.length > 3} /> : null}
                            <Toolbar toolBar={finalToolBar} />
                        </div>
                    </div>
                </div>
                <Divider />
            </div>
        </HeaderToolbarContext.Provider>
    );
};

export default React.memo(HeaderToolbar);
